// LAYOUT-SPEC §0 — Awaiting approval (Deliverables ?awaiting=1[&team=<id>][&duty=<id>])
//
//   ONE QUESTION  Which proposals is a second person still owed a decision on?
//   PRIMARY OBJECT  The program's open not-owed and COR proposals (D111).
//   PRIMARY VERB    Approve all; one row, or the selected rows, second.
//
// Rows are ListOccurrenceDecisions(open_only) program-wide, the same read the
// team tile, the home Do-next row, the page banners and the date rows'
// "Awaiting approval" marks use. The proposer can never approve their own
// proposal: the server refuses it, and the row mirrors that refusal.
//
// DateWaiversSection is the same rows and actions for deliverable waivers
// only, open and approved, on the Waivers & Tailoring page.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraEmptyState, SpecraEntityCell, SpecraInlineBanner, SpecraLink, SpecraListPage, SpecraOverlayDialog, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { useCmAccess } from '../access';
import { useCmDrawerParams } from '../drawerParams';
import { useApproveDecision, useAwaitingApproval, useOccurrenceDecisions, useRejectDecision } from '../hooks';
import { cmDate, instantDay } from '../lib/format';
import { useCmLookups, useUserNames } from '../lookups';
import { CmTable } from './common';
/** The product's names for a proposal's kind. */ export const DECISION_KIND_LABEL = {
    not_owed: 'Not owed',
    waiver: 'Waiver'
};
const NOT_OWED_WHY = {
    not_owed: 'The contract does not require it',
    generated_in_error: 'This date was created in error'
};
export const SELF_PROPOSED_TOOLTIP = 'You proposed this; another person must approve';
/** Deny and withdraw are one click; the server keeps a reason, so this is it. */ export const DENY_REASON = 'No reason given.';
/** How many approvals run at once in a bulk approve. */ const BATCH = 4;
/** The change-set states a proposal is still open in (pangaea's in-flight set). */ const OPEN_STATES = new Set([
    'Draft',
    'Parsed',
    'UnderReview',
    'Approved'
]);
/** The awaiting-approval list's address, optionally for one team or deliverable. */ export function awaitingHref(programId, { team, duty } = {}) {
    const p = new URLSearchParams({
        awaiting: '1'
    });
    if (team) p.set('team', team);
    if (duty) p.set('duty', duty);
    return `/delivery/programs/${programId}/deliverables?${p.toString()}`;
}
/**
 * "n dates awaiting approval — Review" on a page whose dates carry open
 * proposals: the whole program, one team, or one deliverable.
 */ export function AwaitingApprovalBanner({ programId, team, duty }) {
    const { query, occById } = useAwaitingApproval(programId);
    const n = (query.data?.decisions ?? []).filter((d)=>{
        const o = occById.get(d.occurrenceId);
        return (!team || o?.workstreamId === team) && (!duty || o?.dutyId === duty);
    }).length;
    if (n === 0) return null;
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "warning",
        variant: "framed",
        "data-testid": Testids.cmApprovals.banner,
        action: /*#__PURE__*/ _jsx(SpecraLink, {
            href: awaitingHref(programId, {
                team,
                duty
            }),
            renderLink: renderRouterLink,
            "data-testid": Testids.cmApprovals.bannerReview,
            children: "Review"
        }),
        children: [
            n,
            " ",
            n === 1 ? 'date' : 'dates',
            " awaiting approval"
        ]
    });
}
function toRows(decisions, occById, lk, userId) {
    return decisions.map((decision)=>{
        const occ = occById.get(decision.occurrenceId);
        const d = occ ? lk.dutyById.get(occ.dutyId) : undefined;
        const clin = occ ? lk.exerciseById.get(occ.exerciseId)?.clinNumber : undefined;
        return {
            decision,
            occ,
            title: d?.title || 'Deliverable',
            ref: [
                d?.letter,
                clin ? `CLIN ${clin}` : ''
            ].filter(Boolean).join(' · '),
            team: lk.teamName(occ?.workstreamId ?? ''),
            mine: !!userId && decision.proposedByUserId === userId,
            open: OPEN_STATES.has(decision.state)
        };
    });
}
const rowName = (r)=>`${r.title}${r.occ ? ` due ${cmDate(r.occ.dueOn)}` : ''}`;
/**
 * Approve, deny and approve-many over decision rows. Each row is its own
 * approval: in a batch, one failing names itself and the rest still run.
 */ function useApprovalRunner(programId, onApproved) {
    const approveOne = useApproveDecision(programId, {
        success: 'Approved'
    });
    const approveMany = useApproveDecision(programId, {
        errors: 'inline'
    });
    const deny = useRejectDecision(programId);
    const [busy, setBusy] = useState(new Set());
    const [failures, setFailures] = useState([]);
    const markBusy = (ids, on)=>setBusy((prev)=>{
            const next = new Set(prev);
            for (const id of ids){
                if (on) next.add(id);
                else next.delete(id);
            }
            return next;
        });
    const approveRow = (r)=>{
        const id = r.decision.changeSetId;
        markBusy([
            id
        ], true);
        approveOne.mutate({
            changeSetId: id
        }, {
            onSettled: ()=>markBusy([
                    id
                ], false)
        });
    };
    const denyRow = (r)=>{
        const id = r.decision.changeSetId;
        markBusy([
            id
        ], true);
        deny.mutate({
            changeSetId: id,
            reason: DENY_REASON
        }, {
            onSuccess: ()=>toast.success(r.mine ? 'Proposal withdrawn' : 'Proposal denied'),
            onSettled: ()=>markBusy([
                    id
                ], false)
        });
    };
    const approveRows = async (todo)=>{
        setFailures([]);
        markBusy(todo.map((r)=>r.decision.changeSetId), true);
        const failed = [];
        for(let i = 0; i < todo.length; i += BATCH){
            await Promise.all(todo.slice(i, i + BATCH).map(async (r)=>{
                const id = r.decision.changeSetId;
                try {
                    await approveMany.mutateAsync({
                        changeSetId: id
                    });
                    onApproved?.(id);
                } catch (err) {
                    failed.push({
                        name: rowName(r),
                        error: apiErrorMessage(err, 'Could not approve')
                    });
                } finally{
                    markBusy([
                        id
                    ], false);
                }
            }));
        }
        setFailures(failed);
        const ok = todo.length - failed.length;
        if (failed.length === 0) toast.success(`Approved ${ok}`);
        else toast.error(`Approved ${ok} of ${todo.length}; ${failed.length} could not be approved`);
    };
    return {
        busy,
        running: busy.size > 0,
        failures,
        clearFailures: ()=>setFailures([]),
        approveRow,
        denyRow,
        approveRows
    };
}
/** Approve (disabled on your own, with the reason) and one-click Deny / Withdraw. */ function DecisionActions({ r, runner }) {
    const approve = /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        disabled: r.mine || runner.running,
        isLoading: runner.busy.has(r.decision.changeSetId),
        onClick: ()=>runner.approveRow(r),
        "data-testid": Testids.cmApprovals.approve,
        children: "Approve"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex justify-end gap-2",
        children: [
            r.mine ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: SELF_PROPOSED_TOOLTIP,
                children: /*#__PURE__*/ _jsx("span", {
                    children: approve
                })
            }) : approve,
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                disabled: runner.running,
                onClick: ()=>runner.denyRow(r),
                "data-testid": Testids.cmApprovals.reject,
                children: r.mine ? 'Withdraw' : 'Deny'
            })
        ]
    });
}
function ApprovalFailures({ runner }) {
    if (runner.failures.length === 0) return null;
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        title: `${runner.failures.length} could not be approved`,
        onDismiss: runner.clearFailures,
        "data-testid": Testids.cmApprovals.failures,
        children: /*#__PURE__*/ _jsx("ul", {
            className: "list-disc pl-5",
            children: runner.failures.map((f)=>/*#__PURE__*/ _jsxs("li", {
                    children: [
                        f.name,
                        ": ",
                        f.error
                    ]
                }, f.name))
        })
    });
}
/** [Approve all (n)] and its confirmation; your own proposals are left out. */ function ApproveAll({ approvable, own, runner }) {
    const [confirming, setConfirming] = useState(false);
    const n = approvable.length;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraButton, {
                disabled: n === 0 || runner.running,
                onClick: ()=>setConfirming(true),
                "data-testid": Testids.cmApprovals.approveAll,
                children: [
                    "Approve all (",
                    n,
                    ")"
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: confirming,
                onOpenChange: setConfirming,
                size: "sm",
                title: `Approve all ${n} ${n === 1 ? 'proposal' : 'proposals'}?`,
                testId: Testids.cmApprovals.approveAllDialog,
                footerProps: {
                    confirmLabel: `Approve all (${n})`,
                    onCancel: ()=>setConfirming(false),
                    onConfirm: ()=>{
                        setConfirming(false);
                        void runner.approveRows(approvable);
                    },
                    confirmProps: {
                        'data-testid': Testids.cmApprovals.approveAllConfirm
                    }
                },
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "body-ui",
                    children: [
                        "Each takes effect on its date now.",
                        own > 0 ? ` ${own} you proposed ${own === 1 ? 'is' : 'are'} left for someone else.` : ''
                    ]
                })
            })
        ]
    });
}
function baseColumns(names) {
    return [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: r.title,
                    description: r.ref || undefined
                })
        },
        {
            id: 'team',
            header: 'Team',
            cell: (r)=>r.team
        },
        {
            id: 'due',
            header: 'Due',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(r.occ?.dueOn, '—')
                })
        },
        {
            id: 'by',
            header: 'Proposed by',
            cell: (r)=>names(r.decision.proposedByUserId)
        },
        {
            id: 'on',
            header: 'Proposed on',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(instantDay(r.decision.proposedAt), '—')
                })
        },
        {
            id: 'reason',
            header: 'Reason',
            cell: (r)=>{
                const d = r.decision;
                const why = d.kind === 'not_owed' ? d.note || NOT_OWED_WHY[d.reason] || d.reason : d.reason;
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: why || '—'
                });
            }
        }
    ];
}
export function AwaitingApprovalView({ programId }) {
    const [sp, setSp] = useSearchParams();
    const team = sp.get('team') ?? '';
    const duty = sp.get('duty') ?? '';
    const lk = useCmLookups(programId);
    const names = useUserNames();
    const access = useCmAccess(programId);
    const drawer = useCmDrawerParams();
    const { user } = useAuth();
    const { query: q, occById } = useAwaitingApproval(programId);
    const [selected, setSelected] = useState(new Set());
    const toggle = (id, on)=>setSelected((prev)=>{
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });
    const runner = useApprovalRunner(programId, (id)=>toggle(id, false));
    const rows = useMemo(()=>toRows(q.data?.decisions ?? [], occById, lk, user?.id).filter((r)=>(!team || r.occ?.workstreamId === team) && (!duty || r.occ?.dutyId === duty)), [
        q.data,
        occById,
        lk,
        user?.id,
        team,
        duty
    ]);
    const canAct = access.canWrite;
    const approvable = rows.filter((r)=>!r.mine);
    // Selection survives a refetch only for rows still listed and approvable.
    const picked = approvable.filter((r)=>selected.has(r.decision.changeSetId));
    const allPicked = approvable.length > 0 && picked.length === approvable.length;
    const setParam = (key, value)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (value) n.set(key, value);
            else n.delete(key);
            return n;
        }, {
            replace: true
        });
    const base = baseColumns(names);
    const columns = [
        ...canAct ? [
            {
                id: 'select',
                header: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                    "aria-label": "Select every proposal you can approve",
                    checked: allPicked ? true : picked.length > 0 ? 'indeterminate' : false,
                    disabled: approvable.length === 0 || runner.running,
                    onCheckedChange: (v)=>setSelected(v === true ? new Set(approvable.map((r)=>r.decision.changeSetId)) : new Set()),
                    "data-testid": Testids.cmApprovals.selectAll
                }),
                cell: (r)=>/*#__PURE__*/ _jsx(SpecraCheckbox, {
                        "aria-label": `Select ${rowName(r)}`,
                        checked: !r.mine && selected.has(r.decision.changeSetId),
                        disabled: r.mine || runner.running,
                        onCheckedChange: (v)=>toggle(r.decision.changeSetId, v === true),
                        "data-testid": Testids.cmApprovals.select
                    })
            }
        ] : [],
        ...base.slice(0, 3),
        {
            id: 'kind',
            header: 'Kind',
            cell: (r)=>DECISION_KIND_LABEL[r.decision.kind] ?? 'Proposal'
        },
        ...base.slice(3)
    ];
    return /*#__PURE__*/ _jsxs(SpecraListPage, {
        "data-testid": Testids.cmApprovals.page,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            eyebrow: /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/delivery/programs/${programId}/deliverables`,
                renderLink: renderRouterLink,
                children: "Deliverables"
            }),
            title: "Awaiting approval",
            meta: q.data ? rows.length : undefined,
            description: "Proposals a second person must approve. Whoever proposed one cannot approve it.",
            actions: canAct ? /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    picked.length > 0 ? /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        disabled: runner.running,
                        onClick: ()=>void runner.approveRows(picked),
                        "data-testid": Testids.cmApprovals.approveSelected,
                        children: [
                            "Approve selected (",
                            picked.length,
                            ")"
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(ApproveAll, {
                        approvable: approvable,
                        own: rows.length - approvable.length,
                        runner: runner
                    })
                ]
            }) : null
        }),
        toolbarProps: {
            search: false,
            filters: /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Team",
                className: "w-56",
                value: team,
                onChange: (e)=>setParam('team', e.target.value),
                "data-testid": Testids.cmApprovals.teamFilter,
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "All teams"
                    }),
                    lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                            value: w.id,
                            children: w.name
                        }, w.id))
                ]
            }),
            actions: duty ? /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "ghost",
                onClick: ()=>setParam('duty', ''),
                children: [
                    lk.dutyById.get(duty)?.title ?? 'One deliverable',
                    " · Show all"
                ]
            }) : undefined,
            resultSummary: `${rows.length} awaiting`
        },
        children: [
            /*#__PURE__*/ _jsx(ApprovalFailures, {
                runner: runner
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Proposals awaiting approval",
                rows: rows,
                columns: columns,
                rowKey: (r)=>r.decision.changeSetId,
                rowTestId: Testids.cmApprovals.row,
                isLoading: q.isLoading || lk.isLoading,
                error: q.error,
                onRetry: ()=>void q.refetch(),
                onRowClick: (r)=>drawer.open('occ', r.decision.occurrenceId),
                getRowLabel: (r)=>`Open ${rowName(r)}`,
                rowActions: canAct ? (r)=>/*#__PURE__*/ _jsx(DecisionActions, {
                        r: r,
                        runner: runner
                    }) : undefined,
                empty: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.cmApprovals.empty,
                    children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        framed: true,
                        title: "Nothing is awaiting approval.",
                        description: "Proposals appear here when someone marks a date not owed."
                    })
                })
            })
        ]
    });
}
/**
 * Deliverable waivers, open proposals and approved ones,
 * for the Waivers & Tailoring page: approve one, approve all, or deny.
 */ export function DateWaiversSection({ programId }) {
    const lk = useCmLookups(programId);
    const names = useUserNames();
    const access = useCmAccess(programId);
    const drawer = useCmDrawerParams();
    const { user } = useAuth();
    const q = useOccurrenceDecisions(programId, {});
    const runner = useApprovalRunner(programId);
    const rows = useMemo(()=>{
        const occById = new Map((q.data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        const waivers = (q.data?.decisions ?? []).filter((d)=>d.kind === 'waiver' && (d.state === 'Applied' || OPEN_STATES.has(d.state)));
        return toRows(waivers, occById, lk, user?.id);
    }, [
        q.data,
        lk,
        user?.id
    ]);
    if (!q.isLoading && rows.length === 0) return null;
    const open = rows.filter((r)=>r.open);
    const approvable = open.filter((r)=>!r.mine);
    const canAct = access.canWrite;
    const base = baseColumns(names);
    const columns = [
        ...base.slice(0, 3),
        {
            id: 'cor',
            header: 'COR date',
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: r.decision.waivedOn ? cmDate(r.decision.waivedOn) : 'date not recorded'
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (r)=>r.open ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "warning",
                    children: "Awaiting approval"
                }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "success",
                    children: "Approved"
                })
        },
        ...base.slice(3)
    ];
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": Testids.cmApprovals.dateWaivers,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: `Deliverable waivers · ${open.length} awaiting approval · ${rows.length - open.length} approved`,
                action: canAct && open.length > 0 ? /*#__PURE__*/ _jsx(ApproveAll, {
                    approvable: approvable,
                    own: open.length - approvable.length,
                    runner: runner
                }) : undefined
            }),
            /*#__PURE__*/ _jsx(ApprovalFailures, {
                runner: runner
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Deliverable waivers",
                rows: rows,
                columns: columns,
                rowKey: (r)=>r.decision.changeSetId,
                rowTestId: Testids.cmApprovals.row,
                isLoading: q.isLoading || lk.isLoading,
                error: q.error,
                onRetry: ()=>void q.refetch(),
                onRowClick: (r)=>drawer.open('occ', r.decision.occurrenceId),
                getRowLabel: (r)=>`Open ${rowName(r)}`,
                rowActions: canAct ? (r)=>r.open ? /*#__PURE__*/ _jsx(DecisionActions, {
                        r: r,
                        runner: runner
                    }) : null : undefined
            })
        ]
    });
}
